import { Directive, ElementRef, OnInit, OnDestroy, inject, Renderer2, PLATFORM_ID, input } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export interface SkipLinkConfig {
  /** Target selector to skip to */
  target: string;
  /** Static label for the skip link. Prefer `labelKey` for i18n. */
  label?: string;
  /** Translation key for the label; resolved via TranslateService and kept in sync on language change. */
  labelKey?: string;
  /** Position: 'top' (default) or 'before-element' */
  position?: 'top' | 'before-element';
  /** Custom CSS classes for styling */
  customClasses?: string;
  /** Whether to show skip link only on focus */
  showOnFocusOnly?: boolean;
}

@Directive({
  selector: '[gsSkipLinks]',
  standalone: true,
})
/**
 * Renders accessible skip links to jump directly to key content regions.
 * Can be positioned at the top or directly before a specific element.
 */
export class SkipLinksDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly translate = inject(TranslateService);
  /** Skip links are a browser-only DOM feature; suppress all DOM work during SSR/prerender. */
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Inputs
  /** Single or multiple skip link configurations. */
  readonly gsSkipLinks = input<SkipLinkConfig | SkipLinkConfig[]>([]);

  private skipLinksContainer?: HTMLElement;
  private createdLinks: HTMLElement[] = [];
  /** Links whose label is driven by a translation key, kept in sync on language change. */
  private translatableLinks: { element: HTMLElement; key: string }[] = [];
  /** Subscription to language changes for live label updates. */
  private langChangeSub?: Subscription;

  /**
   * Lifecycle: Build skip links container and insert into the document.
   */
  ngOnInit(): void {
    // Skip links manipulate document.body directly and are irrelevant to prerendered HTML;
    // build them on the client only (they are re-created at hydration).
    if (!this.isBrowser) return;
    this.createSkipLinks();
    this.updateLabels();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => this.updateLabels());
  }

  /**
   * Lifecycle: Remove the skip links container if created by this directive.
   */
  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
    if (this.skipLinksContainer) {
      this.skipLinksContainer.remove();
    }
  }

  /**
   * Create container and link elements based on the provided configuration.
   */
  private createSkipLinks(): void {
    const gsSkipLinks = this.gsSkipLinks();
    const configs = Array.isArray(gsSkipLinks) ? gsSkipLinks : [gsSkipLinks];
    if (configs.length === 0) return;

    // Create container
    this.skipLinksContainer = this.renderer.createElement('div');
    this.renderer.addClass(this.skipLinksContainer, 'gs-skip-links');
    this.renderer.setAttribute(this.skipLinksContainer, 'role', 'navigation');
    this.renderer.setAttribute(this.skipLinksContainer, 'aria-label', 'Skip links');

    // Default styles for accessibility
    this.renderer.setStyle(this.skipLinksContainer, 'position', 'absolute');
    this.renderer.setStyle(this.skipLinksContainer, 'top', '0');
    this.renderer.setStyle(this.skipLinksContainer, 'left', '0');
    this.renderer.setStyle(this.skipLinksContainer, 'z-index', '9999');

    configs.forEach(config => {
      if (!config.target || (!config.label && !config.labelKey)) {
        console.warn('Skip link requires a target and either a label or labelKey property');
        return;
      }

      const skipLink = this.createSkipLink(config);
      this.renderer.appendChild(this.skipLinksContainer, skipLink);
      this.createdLinks.push(skipLink);
    });

    // Insert container based on position
    const firstConfig = configs[0];
    if (firstConfig.position === 'before-element') {
      this.renderer.insertBefore(
        this.elementRef.nativeElement.parentNode,
        this.skipLinksContainer,
        this.elementRef.nativeElement
      );
    } else {
      // Default: insert at top of body
      this.renderer.insertBefore(
        document.body,
        this.skipLinksContainer,
        document.body.firstChild
      );
    }
  }

  /**
   * Create a single skip link element.
   * @param config configuration including target and label
   * @returns anchor element ready for insertion
   */
  private createSkipLink(config: SkipLinkConfig): HTMLElement {
    const link = this.renderer.createElement('a');
    this.renderer.setAttribute(link, 'href', `#${config.target.replace('#', '')}`);
    // Initial label: static label wins, otherwise show the key until translations resolve in updateLabels().
    this.renderer.setProperty(link, 'textContent', config.label ?? config.labelKey ?? '');
    if (config.labelKey) {
      this.translatableLinks.push({ element: link, key: config.labelKey });
    }

    // Default skip link styles
    this.renderer.addClass(link, 'gs-skip-link');
    this.renderer.setStyle(link, 'position', 'absolute');
    this.renderer.setStyle(link, 'padding', '8px 16px');
    this.renderer.setStyle(link, 'background-color', '#000');
    this.renderer.setStyle(link, 'color', '#fff');
    this.renderer.setStyle(link, 'text-decoration', 'none');
    this.renderer.setStyle(link, 'font-weight', 'bold');
    this.renderer.setStyle(link, 'border-radius', '0 0 4px 4px');
    this.renderer.setStyle(link, 'white-space', 'nowrap');
    this.renderer.setStyle(link, 'z-index', '10000');

    if (config.showOnFocusOnly !== false) {
      // Hide by default, show on focus
      this.renderer.setStyle(link, 'transform', 'translateY(-100%)');
      this.renderer.setStyle(link, 'transition', 'transform 0.2s');

      this.renderer.listen(link, 'focus', () => {
        this.renderer.setStyle(link, 'transform', 'translateY(0)');
      });

      this.renderer.listen(link, 'blur', () => {
        this.renderer.setStyle(link, 'transform', 'translateY(-100%)');
      });
    }

    // Add custom classes if provided
    if (config.customClasses) {
      const classes = config.customClasses.split(' ');
      classes.forEach(className => {
        this.renderer.addClass(link, className.trim());
      });
    }

    // Handle click to ensure target is focusable
    this.renderer.listen(link, 'click', (event: Event) => {
      event.preventDefault();
      const targetElement = document.querySelector(config.target) as HTMLElement;

      if (targetElement) {
        // Make target focusable if it isn't already
        if (!targetElement.hasAttribute('tabindex') && !this.isFocusableElement(targetElement)) {
          this.renderer.setAttribute(targetElement, 'tabindex', '-1');
        }

        targetElement.focus();
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        console.warn(`Skip link target '${config.target}' not found`);
      }
    });

    return link;
  }

  /**
   * Determine if an element is inherently focusable or has focusable attributes.
   * @param element element to check
   */
  private isFocusableElement(element: HTMLElement): boolean {
    const focusableElements = ['input', 'button', 'select', 'textarea', 'a', 'area'];
    return focusableElements.includes(element.tagName.toLowerCase()) ||
      element.hasAttribute('tabindex') ||
      element.hasAttribute('contenteditable');
  }

  /**
   * Resolve all translation-key-driven labels and write them to their link elements.
   * Called on init and whenever the active language changes.
   */
  private updateLabels(): void {
    if (this.translatableLinks.length === 0) return;

    const keys = this.translatableLinks.map(entry => entry.key);
    this.translate.get(keys).subscribe({
      next: (translations: Record<string, string>) => {
        this.translatableLinks.forEach(entry => {
          this.renderer.setProperty(entry.element, 'textContent', translations[entry.key] ?? entry.key);
        });
      }
    });
  }

  /** Add a skip link dynamically at runtime. */
  addSkipLink(config: SkipLinkConfig): void {
    if (!this.skipLinksContainer) {
      this.createSkipLinks();
      return;
    }

    const skipLink = this.createSkipLink(config);
    this.renderer.appendChild(this.skipLinksContainer, skipLink);
    this.createdLinks.push(skipLink);
  }

  /** Remove all dynamically created skip links. */
  removeAllSkipLinks(): void {
    this.createdLinks.forEach(link => {
      if (link.parentNode) {
        this.renderer.removeChild(link.parentNode, link);
      }
    });
    this.createdLinks = [];
  }
}
